import React, { useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Sparkles, Mail, KeyRound, CheckCircle2 } from "lucide-react";

const STATES = [
  "Andhra Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh",
  "Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Odisha","Punjab","Rajasthan",
  "Tamil Nadu","Telangana","Uttar Pradesh","Uttarakhand","West Bengal",
];

export default function SampleDownloadModal({ open, onOpenChange }) {
  const [step, setStep] = useState(1); // 1=criteria, 2=otp, 3=done
  const [email, setEmail] = useState("");
  const [dtype, setDtype] = useState("both");
  const [coverage, setCoverage] = useState("pan_india");
  const [state, setState] = useState("");
  const [otp, setOtp] = useState("");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setStep(1); setEmail(""); setDtype("both"); setCoverage("pan_india"); setState(""); setOtp(""); setToken(""); };
  const close = () => { onOpenChange(false); setTimeout(reset, 300); };

  const requestOtp = async (e) => {
    e.preventDefault();
    if (coverage === "state" && !state) { toast.error("Please pick a state"); return; }
    setBusy(true);
    try {
      await api.post("/public/sample/request-otp", { email: email.trim(), dtype, coverage, state: state || undefined });
      toast.success(`OTP sent to ${email}`);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/public/sample/verify-otp", { email: email.trim(), otp: otp.trim() });
      setToken(data.token);
      // Trigger download immediately
      const r = await fetch(`${API_BASE}/public/sample/download?token=${encodeURIComponent(data.token)}`);
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || `Download failed (${r.status})`); }
      const blob = await r.blob();
      const cd = r.headers.get("content-disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : "RegistryFeed_Sample.csv";
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = fname; a.click(); URL.revokeObjectURL(a.href);
      toast.success("Sample downloaded! We'll email you daily match counts.");
      setStep(3);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-lg bg-popover" data-testid="sample-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Download today's sample</DialogTitle>
          <DialogDescription>
            Up to 50 rows from today's fresh registrations. Verify your email once and grab the file.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <form onSubmit={requestOtp} className="space-y-3">
            <div>
              <Label htmlFor="s-email">Work email</Label>
              <Input id="s-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="sample-email-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Data type</Label>
                <Select value={dtype} onValueChange={setDtype}>
                  <SelectTrigger data-testid="sample-dtype"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="both">GST + MCA</SelectItem>
                    <SelectItem value="gst">GST only</SelectItem>
                    <SelectItem value="mca">MCA only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Coverage</Label>
                <Select value={coverage} onValueChange={setCoverage}>
                  <SelectTrigger data-testid="sample-coverage"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-popover">
                    <SelectItem value="pan_india">Pan India</SelectItem>
                    <SelectItem value="state">State-wise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {coverage === "state" && (
              <div>
                <Label>State</Label>
                <Select value={state} onValueChange={setState}>
                  <SelectTrigger data-testid="sample-state"><SelectValue placeholder="Pick a state" /></SelectTrigger>
                  <SelectContent className="bg-popover max-h-64">
                    {STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter>
              <Button type="submit" disabled={busy} data-testid="sample-request-otp-btn"><Mail className="h-4 w-4 mr-1.5" />{busy ? "Sending..." : "Send OTP"}</Button>
            </DialogFooter>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verifyOtp} className="space-y-3">
            <div className="text-sm text-muted-foreground">We sent a 6-digit code to <span className="num text-foreground">{email}</span>. It expires in 10 minutes.</div>
            <div>
              <Label htmlFor="s-otp">Enter OTP</Label>
              <Input id="s-otp" required maxLength={8} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="123456" className="num tracking-widest text-center text-lg" data-testid="sample-otp-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button type="submit" disabled={busy || otp.length < 4} data-testid="sample-verify-btn"><KeyRound className="h-4 w-4 mr-1.5" />{busy ? "Verifying..." : "Verify & download"}</Button>
            </DialogFooter>
          </form>
        )}

        {step === 3 && (
          <div className="space-y-3 py-2">
            <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
              <div className="font-medium">Sample downloaded!</div>
            </div>
            <p className="text-sm text-muted-foreground">We'll email you a daily count of new records matching your criteria until you subscribe. Ready for full data? Create a free account.</p>
            <DialogFooter>
              <Button onClick={close}>Close</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function SampleDownloadCTA({ variant = "hero", className = "" }) {
  const [open, setOpen] = useState(false);
  const btnClass = variant === "hero"
    ? "bg-emerald-500 hover:bg-emerald-600 text-white btn-shine"
    : "";
  return (
    <>
      <Button size="lg" className={`${btnClass} ${className}`} onClick={() => setOpen(true)} data-testid="download-sample-cta">
        <Download className="h-4 w-4 mr-1.5" /> Download Sample
      </Button>
      <SampleDownloadModal open={open} onOpenChange={setOpen} />
    </>
  );
}
